import React from 'react';
import { Card, Tag, Tooltip, Button } from 'antd';
import { DeleteOutlined, EyeOutlined, UserOutlined, CalendarOutlined, PhoneOutlined, EnvironmentOutlined, DollarOutlined, CheckOutlined } from '@ant-design/icons';
import moment from 'moment';
import './SheetOrderCard.css';
import { validePhoneSheetRegex } from '../../constants/others';

const SheetOrderCard = ({
  order,
  onView,
  onDelete,
  onMarkTreated,
  onConfirmOrder,
  showActions = true,
  showDeleteAction = false,
  showMarkTreatedAction = false,
  showConfirmOrderAction = false,
  showOrderDate = false,
  userRole = null
}) => {
  const {
    id,
    name,
    phone,
    address,
    city,
    price,
    orderDate,
    unit,
    productName,
    createdAt,
    isTreated,
    user,
    confirmationDate,
    confirmationEnd,
    confirmationStatus,
    isDuplicate
  } = order;

  // Calculate confirmation duration for super admin
  const getConfirmationDuration = () => {
    if (!confirmationDate || !confirmationEnd) return null;

    const start = moment(confirmationDate);
    const end = moment(confirmationEnd);
    const duration = moment.duration(end.diff(start));

    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const seconds = duration.seconds();

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  const cardActions = [];
  const isValidPhone = validePhoneSheetRegex.test(phone);
  if (showActions) {
    cardActions.push(
      <Tooltip title="Voir les détails" key="view">
        <EyeOutlined onClick={() => onView(order)} />
      </Tooltip>
    );

    if (showMarkTreatedAction && !isTreated) {
      cardActions.push(
        <Tooltip title="Marquer comme traité" key="mark-treated">
          <CheckOutlined onClick={() => onMarkTreated(id)} style={{ color: '#52c41a' }} />
        </Tooltip>
      );
    }

    if (showConfirmOrderAction && !isTreated) {
      cardActions.push(
        <Tooltip title="Confirmer la commande" key="confirm-order">
          <Button
            type="primary"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => onConfirmOrder(order)}
            style={{ marginLeft: '8px' }}
          >
            Confirmer
          </Button>
        </Tooltip>
      );
    }

    if (showDeleteAction) {
      cardActions.push(
        <Tooltip title="Supprimer" key="delete">
          <DeleteOutlined onClick={() => onDelete(id)} style={{ color: '#ff4d4f' }} />
        </Tooltip>
      );
    }
  }

  return (
    <Card
      className={`sheet-order-card ${isValidPhone ? 'valid-phone' : 'invalid-phone'}`}
      hoverable
      actions={cardActions.length > 0 ? cardActions : undefined}
    >
      <div className="card-header">
        <div className="customer-info">
          <h3 className="customer-name">
            <UserOutlined /> {name}
          </h3>
          <div className="contact-info">
            <span className="phone">
              <PhoneOutlined /> {phone}
            </span>
          </div>
        </div>
        <div className="status-info">
          <Tag color={isTreated ? "green" : "orange"}>
            {isTreated && confirmationStatus ? confirmationStatus : "En attente"}
          </Tag>
          {user && (
            <Tag color="blue">
              Assigné à: {user.name}
            </Tag>
          )}
          {isDuplicate > 0 && (
            <Tag color="red">
              Duplicata: {isDuplicate}
            </Tag>
          )}
        </div>
      </div>

      <div className="card-content">
        <div className="info-row">
          <span className="info-item">
            <EnvironmentOutlined />
            <span className="info-label">Adresse:</span>
            <span className="info-value">{address}, {city}</span>
          </span>
        </div>

        <div className="info-row">
          <span className="info-item">
            <DollarOutlined />
            <span className="info-label">Prix:</span>
            <span className="info-value price">{price} DH</span>
          </span>
          <span className="info-item">
            <span className="info-label">Unité:</span>
            <span className="info-value">{unit}</span>
          </span>
        </div>

        <div className="info-row">
          <span className="info-item">
            <span className="info-label">Produit:</span>
            <Tag color="blue">{productName || 'N/A'}</Tag>
          </span>
        </div>

        {showOrderDate && (
          <div className="info-row">
            <span className="info-item">
              <CalendarOutlined />
              <span className="info-label">Date commande:</span>
              <span className="info-value">{moment(orderDate).format('DD/MM/YYYY')}</span>
            </span>
          </div>
        )}

        {userRole === 'super admin' && (confirmationDate || confirmationEnd) && (
          <div className="info-row confirmation-timing">
            {confirmationDate && (
              <span className="info-item">
                <span className="info-label">Début confirmation:</span>
                <span className="info-value">{moment(confirmationDate).format('DD/MM/YYYY HH:mm:ss')}</span>
              </span>
            )}
            {confirmationEnd && (
              <span className="info-item">
                <span className="info-label">Fin confirmation:</span>
                <span className="info-value">{moment(confirmationEnd).format('DD/MM/YYYY HH:mm:ss')}</span>
              </span>
            )}
            {confirmationDate && confirmationEnd && (
              <span className="info-item duration">
                <span className="info-label">Durée traitement:</span><br />
                <span className="info-value duration-value">{getConfirmationDuration()}</span>
              </span>
            )}
          </div>
        )}

        {userRole === 'super admin' && order.assignments && order?.assignments?.length > 0 && (
          <div className="info-row assignments-section">
            <span className="info-label">Historique des affectations:</span>
            <div className="assignments-list">
              {order.assignments.map((assignment, index) => (
                <div key={index} className="assignment-item">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignContent: 'flex-start', alignItems: 'flex-start' }}>
                    <Tag color="purple">
                      {assignment.user?.name || 'Utilisateur inconnu'}
                    </Tag>
                    <Tag color="orange">
                      {assignment.confirmationStatus || 'Statut inconnu'}
                    </Tag>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignContent: 'flex-start', alignItems: 'flex-start' }}>
                    <span className="assignment-date">
                      {moment(assignment.createdAt).format('DD/MM/YYYY HH:mm')}
                    </span>
                    {assignment.note && <span>{assignment.note}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card-footer">
          <span className="created-date">
            Créé le {moment(createdAt).format('DD/MM/YYYY HH:mm')}
          </span>
        </div>
      </div>
    </Card>
  );
};

export default SheetOrderCard;
